import React, { useState, useEffect, useContext, useCallback } from "react";
import { useHistory } from "react-router-dom";
import {
  Container,
  Row,
  Col,
  Table,
  Button,
  Card,
  CardHeader,
  CardTitle,
  CardBody,
  Spinner,
  Input,
  FormGroup,
  Label,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Form
} from "reactstrap";
import Pagination from "react-js-pagination";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { 
    faUsers, 
    faSync, 
    faLock, 
    faFileExcel 
} from "@fortawesome/free-solid-svg-icons";
import ReactBSAlert from "react-bootstrap-sweetalert"; // 1. Import Added
import projectUsersService from "../../services/api/project-users-service.js";
import ProjectContext from "../../ProjectContext.js";
import UserContext from "../../UserContext.js";
import moment from "moment";
import { debounce } from "lodash";
import grievanceService from "../../services/api/grievance-service.js";

const GrievancePermissionsListing = () => {
  const history = useHistory();
  const projectDetail = useContext(ProjectContext);
  const rawUserCtx = useContext(UserContext);
  const projectId = projectDetail.id;
  if(projectId ===''){
    history.push("/project/allprojects");
  }

  // --- Role & Access Logic ---
  const currentUser = rawUserCtx?.userdetails || rawUserCtx;
  const rolesFromProject = projectDetail?.current_user_roles || [];
  const rolesFromUser = currentUser?.current_user_roles || currentUser?.roles || [];
  const userRoles = rolesFromProject.length ? rolesFromProject : rolesFromUser;
  const canManageUsers = userRoles.includes("admin") || userRoles.includes("owner");

  // --- State ---
  const [users, setUsers] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [updatingId, setUpdatingId] = useState(null);
  const [alert, setAlert] = useState(null); // 2. Alert State Added

  // Filter State
  const [filters, setFilters] = useState({
    email: "",
    first_name: "",
    last_name: "",
    start_date: null,
    end_date: null,
  });
  const [validationErrors, setValidationErrors] = useState({});

  // Pagination State
  const [pagination, setPagination] = useState({
    currentPage: 1,
    pageSize: 10,
    totalItems: 0,
  });

  // Export State
  const [exportModalOpen, setExportModalOpen] = useState(false);
  const [exportFields, setExportFields] = useState(["name", "createdAt"]);
  const [exportError, setExportError] = useState("");

  const availableExportFields = [
    { label: "Full Name", key: "name" },
    { label: "First Name", key: "first_name" },
    { label: "Last Name", key: "last_name" },
    { label: "Email Address", key: "email" },
    { label: "Contact Number", key: "phone" },
    { label: "Created At", key: "createdAt" },
  ];

  const firstRecord = (pagination.currentPage - 1) * pagination.pageSize + 1;

  // --- Helpers ---

  // Validate Dates
  const validateFilters = () => {
    const errors = {};
    if (filters.start_date && filters.end_date) {
      const startDate = new Date(filters.start_date);
      const endDate = new Date(filters.end_date);

      if (startDate > endDate) {
        errors.end_date = "End date must be after start date";
        setValidationErrors(errors);
        return false;
      }
    }
    setValidationErrors({});
    return true;
  };

  // Debounce API calls for text inputs
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedFetch = useCallback(
    debounce(() => {
        setPagination((prev) => ({ ...prev, currentPage: 1 }));
    }, 500),
    []
  );

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
    if(field !== 'start_date' && field !== 'end_date'){
        debouncedFetch();
    } else {
        setPagination((prev) => ({ ...prev, currentPage: 1 }));
    }
  };

  const handleReset = () => {
    setFilters({
      email: "",
      first_name: "",
      last_name: "",
      start_date: null,
      end_date: null,
    });
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
    setExportError("");
    setExportFields(["name", "createdAt"]);
  };

  // --- API Actions ---

  const getUsers = async () => {
    if (!canManageUsers || !projectId) return;
    if (!validateFilters()) return;

    setIsLoading(true);
    setError(null);
    try {
      const result = await projectUsersService.fetchUsers({
        project_id: projectId,
        first_name: filters.first_name,
        last_name: filters.last_name,
        email: filters.email,
        start_date: filters.start_date || undefined,
        end_date: filters.end_date || undefined,
        page: pagination.currentPage,
        page_size: pagination.pageSize,
        include_grievance_details: true
      });

      if (result && result.status === true && result.data) {
        setUsers(result.data.users || []);
        setPagination((prev) => ({
          ...prev,
          totalItems: result.data.count || 0,
        }));
      } else {
        setUsers([]);
        setPagination((prev) => ({ ...prev, totalItems: 0 }));
      }
    } catch (err) {
      console.log("Error fetching users:", err);
      setError("Error fetching users");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const timer = setTimeout(() => {
        getUsers();
    }, 300); 
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, pagination.currentPage, filters, canManageUsers]);


  // 3. New Function: Confirmation Popup
  const confirmToggleStatus = (userProfileId, publicUserId, currentStatus, userName) => {
    if (!publicUserId) return;
    
    const action = currentStatus === 'active' ? 'Suspend' : 'Restore';
    
    setAlert(
      <ReactBSAlert
        warning
        style={{ display: "block", marginTop: "-100px" }}
        title={`Are you sure?`}
        onConfirm={() => performStatusUpdate(userProfileId, publicUserId, currentStatus, action)}
        onCancel={() => setAlert(null)}
        confirmBtnBsStyle="info"
        cancelBtnBsStyle="danger"
        confirmBtnText={`Yes, ${action} it!`}
        cancelBtnText="Cancel"
        showCancel
      >
        <p>
            You are about to <strong>{action.toLowerCase()}</strong> access for user: <br/>
            {userName ? <b>{userName}</b> : "this user"}
        </p>
      </ReactBSAlert>
    );
  };

  // 4. New Function: Actual API Logic
  const performStatusUpdate = async (userProfileId, publicUserId, currentStatus, actionLabel) => {
    // Show Loading
    setAlert(
        <ReactBSAlert
          info
          style={{ display: "block", marginTop: "-100px" }}
          title={`${actionLabel}ing...`}
          showConfirm={false}
        />
    );
    
    // setUpdatingId(userProfileId); // Optional: if you want the button to spin too, though the Alert covers it
    const newStatus = currentStatus === 'active' ? 'inactive' : 'active';

    try {
      const response = await grievanceService.updateUserGrievanceStatus(publicUserId, newStatus);
      if (response && response.status) {
        
        // Update Local State
        setUsers(prevUsers => 
            prevUsers.map(u => {
                if (u._id === userProfileId) {
                    return {
                        ...u,
                        user_id: {
                            ...u.user_id,
                            grievance_status: newStatus
                        }
                    };
                }
                return u;
            })
        );

        // Show Success
        setAlert(
            <ReactBSAlert
              success
              style={{ display: "block", marginTop: "-100px" }}
              title="Success"
              onConfirm={() => setAlert(null)}
              confirmBtnBsStyle="success"
            >
              User has been {actionLabel.toLowerCase()}ed successfully.
            </ReactBSAlert>
        );

      } else {
        setAlert(
            <ReactBSAlert
              danger
              style={{ display: "block", marginTop: "-100px" }}
              title="Failed"
              onConfirm={() => setAlert(null)}
              confirmBtnBsStyle="danger"
            >
              Failed to update status.
            </ReactBSAlert>
        );
      }
    } catch (err) {
      console.log(err);
      setAlert(
        <ReactBSAlert
          danger
          style={{ display: "block", marginTop: "-100px" }}
          title="Error"
          onConfirm={() => setAlert(null)}
          confirmBtnBsStyle="danger"
        >
          An error occurred while updating status.
        </ReactBSAlert>
      );
    } finally {
      setUpdatingId(null);
    }
  };


  // --- Export Logic ---
  const toggleExportModal = () => {
    setExportModalOpen(!exportModalOpen);
    setExportError("");
  };

  const handleFieldSelection = (fieldKey) => {
    setExportFields((prev) =>
      prev.includes(fieldKey)
        ? prev.filter((k) => k !== fieldKey)
        : [...prev, fieldKey]
    );
  };

  const handleSelectAll = () => {
    setExportFields((prev) =>
      prev.length === availableExportFields.length
        ? []
        : availableExportFields.map((f) => f.key)
    );
  };

  const handleExport = async () => {
    if (users.length === 0) {
      setExportError("No users found to export");
      return;
    }
    if (exportFields.length === 0) {
      setExportError("Please select at least one field.");
      return;
    }

    let startDate = filters.start_date ? moment(filters.start_date) : moment().subtract(2, "months");
    let endDate = filters.end_date ? moment(filters.end_date) : moment();

    try {
      const response = await projectUsersService.exportUsers({
        fields: exportFields,
        project_id: projectId,
        first_name: filters.first_name,
        last_name: filters.last_name,
        email: filters.email,
        start_date: startDate.format("YYYY-MM-DD"),
        end_date: endDate.format("YYYY-MM-DD"),
      });

      const blob = new Blob([response.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const fileName = `GrievanceUsers-${projectDetail.name}-${startDate.format("YYYYMMDD")}.xlsx`;
      const downloadLink = document.createElement("a");
      downloadLink.href = window.URL.createObjectURL(blob);
      downloadLink.download = fileName;
      downloadLink.click();

      toggleExportModal();
    } catch (error) {
      console.log("Export error:", error);
      setExportError("Failed to export users");
    }
  };

  // --- RENDER: ACCESS DENIED ---
  if (!canManageUsers) {
    return (
        <div className="content mt-2">
            <Container fluid className="py-4">
                <Card className="shadow-lg border-0 mt-4">
                    <CardBody className="p-5 text-center">
                        <div className="mb-4">
                            <div className="bg-light rounded-circle d-inline-flex align-items-center justify-content-center" style={{ width: '80px', height: '80px' }}>
                                <FontAwesomeIcon icon={faLock} className="text-muted" size="2x" />
                            </div>
                        </div>
                        <h3 className="mb-3 text-dark">Access Restricted</h3>
                        <p className="text-muted mb-4">
                            Only <strong>Admins</strong> and <strong>Owners</strong> can access this page.
                        </p>
                        <Button color="primary" onClick={() => history.push("/dashboard/home?")}>
                            Go to Dashboard
                        </Button>
                    </CardBody>
                </Card>
            </Container>
        </div>
    );
  }

  // --- RENDER: AUTHORIZED ---
  return (
    <div className="content mt-2">
      {alert} 
      <Container fluid className="py-4">
        
        {/* Filters Card */}
        <Card className="shadow-lg mb-4">
          <CardHeader className="bg-white border-bottom-0 p-4">
            <div className="d-flex align-items-center justify-content-between">
              <CardTitle tag="h2" className="mb-0">
                <FontAwesomeIcon icon={faUsers} className="me-3 text-primary" />
                Grievance User Access
              </CardTitle>
            </div>
          </CardHeader>
          <CardBody>
            <Form onSubmit={(e) => e.preventDefault()}>
                <Row>
                    <Col md={4}>
                        <FormGroup>
                            <Label>Email</Label>
                            <Input 
                                type="text" 
                                placeholder="Enter Email"
                                value={filters.email}
                                onChange={(e) => handleFilterChange('email', e.target.value)}
                            />
                        </FormGroup>
                    </Col>
                    <Col md={4}>
                        <FormGroup>
                            <Label>First Name</Label>
                            <Input 
                                type="text" 
                                placeholder="Enter First Name"
                                value={filters.first_name}
                                onChange={(e) => handleFilterChange('first_name', e.target.value)}
                            />
                        </FormGroup>
                    </Col>
                    <Col md={4}>
                        <FormGroup>
                            <Label>Last Name</Label>
                            <Input 
                                type="text" 
                                placeholder="Enter Last Name"
                                value={filters.last_name}
                                onChange={(e) => handleFilterChange('last_name', e.target.value)}
                            />
                        </FormGroup>
                    </Col>
                </Row>
                <Row>
                    <Col md={4}>
                        <FormGroup>
                            <Label>Start Date</Label>
                            <Input 
                                type="date"
                                value={filters.start_date || ""}
                                onChange={(e) => handleFilterChange('start_date', e.target.value)}
                            />
                        </FormGroup>
                    </Col>
                    <Col md={4}>
                        <FormGroup>
                            <Label>End Date</Label>
                            <Input 
                                type="date"
                                value={filters.end_date || ""}
                                onChange={(e) => handleFilterChange('end_date', e.target.value)}
                                invalid={!!validationErrors.end_date}
                            />
                            {validationErrors.end_date && (
                                <div className="text-danger small">{validationErrors.end_date}</div>
                            )}
                        </FormGroup>
                    </Col>
                    <Col md={4} className="d-flex align-items-center gap-2 mt-3">
                        <Button color="secondary" onClick={handleReset} size="sm">
                            <FontAwesomeIcon icon={faSync} className="me-2" /> Reset
                        </Button>
                        <Button color="success" onClick={toggleExportModal} size="sm">
                            <FontAwesomeIcon icon={faFileExcel} className="me-2" /> Excel
                        </Button>
                    </Col>
                </Row>
            </Form>
          </CardBody>
        </Card>

        {/* Users Table */}
        <Card className="shadow-lg">
          <CardBody>
            {pagination.totalItems > 0 && (
                <Row className="mb-2">
                     <Col md="12" className="text-end">
                        <b>{firstRecord} - {Math.min(pagination.currentPage * pagination.pageSize, pagination.totalItems)} of {pagination.totalItems}</b>
                     </Col>
                </Row>
            )}
            
            {isLoading ? (
              <div className="text-center py-5">
                <Spinner color="primary" />
                <p className="mt-2">Loading Users...</p>
              </div>
            ) : (
              <Table responsive hover striped>
                <thead className="thead-light text-center">
                  <tr>
                    <th>S.No</th>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Phone</th>
                    <th>Created On</th>
                    <th>Grievance Status</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody className="text-center">
                  {users.length > 0 ? (
                    users.map((user, index) => {
                        const publicUser = user.user_id || {}; 
                        const status = publicUser.grievance_status || 'active';
                        const userName = `${user.first_name ?? ""} ${user.last_name ?? ""}`;
                        
                        return (
                        <tr key={user._id}>
                          <td>{firstRecord + index}</td>
                          <td>
                              {user.first_name || user.last_name 
                                ? userName
                                : (user.name || publicUser.name || "-")}
                          </td>
                          <td>{publicUser.email || user.email || "-"}</td>
                          <td>{publicUser.phone_number || user.phone_number || "-"}</td>
                          <td>
                              {new Date(user.createdAt).toLocaleDateString("en-US", {
                                  year: "numeric", month: "short", day: "numeric"
                              })}
                          </td>
                          <td>
                              <span className={`badge ${status === 'active' ? 'bg-success' : 'bg-danger'}`}>
                                  {status.toUpperCase()}
                              </span>
                          </td>
                          <td>
                            <Button
                              size="sm"
                              color={status === 'active' ? 'danger' : 'success'}
                              disabled={updatingId === user._id}
                              // Updated onClick to trigger popup
                              onClick={() => confirmToggleStatus(user._id, publicUser._id, status, userName)}
                            >
                              {updatingId === user._id ? <Spinner size="sm" /> : (
                                  status === 'active' ? "Suspend" : "Restore"
                              )}
                            </Button>
                          </td>
                        </tr>
                      )})
                  ) : (
                    <tr>
                      <td colSpan="7">No users found</td>
                    </tr>
                  )}
                </tbody>
              </Table>
            )}

            {/* Pagination */}
            {pagination.totalItems > 0 && (
              <Row>
                <Col md="12">
                  <div className="d-flex justify-content-end">
                    <Pagination
                      activePage={pagination.currentPage}
                      itemsCountPerPage={pagination.pageSize}
                      totalItemsCount={pagination.totalItems}
                      pageRangeDisplayed={5}
                      onChange={(pageNumber) =>
                        setPagination((prev) => ({ ...prev, currentPage: pageNumber }))
                      }
                      itemClass="page-item"
                      linkClass="page-link"
                    />
                  </div>
                </Col>
              </Row>
            )}
          </CardBody>
        </Card>

        {/* Export Modal */}
        <Modal isOpen={exportModalOpen} centered>
          <ModalHeader className="bg-light text-center">
            Export User Data
          </ModalHeader>
          <ModalBody>
            <FormGroup>
              <Label className="fw-bold mb-2">Select Fields to Add in Excel File</Label>
              <div className="border rounded p-2 overflow-auto" style={{ maxHeight: "200px" }}>
                <div className="fs mb-2">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id="selectAll"
                    onChange={handleSelectAll}
                    checked={exportFields.length === availableExportFields.length}
                    style={{ marginTop: "3px" }}
                  />
                  <label className="form-check-label ms-2" htmlFor="selectAll">Select All</label>
                </div>
                {availableExportFields.map((field) => (
                  <div className="fs" key={field.key}>
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id={field.key}
                      onChange={() => handleFieldSelection(field.key)}
                      checked={exportFields.includes(field.key)}
                      style={{ marginTop: "3px" }}
                    />
                    <Label className="form-check-label ms-2" htmlFor={field.key}>{field.label}</Label>
                  </div>
                ))}
              </div>
            </FormGroup>
            {exportError && <div className="text-danger text-center mt-3 small">{exportError}</div>}
          </ModalBody>
          <ModalFooter className="d-flex justify-content-center">
            <Button color="primary" onClick={handleExport}>Export</Button>
            <Button color="secondary" onClick={toggleExportModal}>Cancel</Button>
          </ModalFooter>
        </Modal>

      </Container>
    </div>
  );
};

export default GrievancePermissionsListing;